import React, { useState } from 'react';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import {
    Calendar,
    Clock,
    MapPin,
    MoreHorizontal,
    Download,
    BookOpen,
    Users,
    ChevronLeft,
    ChevronRight,
    Printer,
} from 'lucide-react';

export default function Timetable() {
    const [activeDay, setActiveDay] = useState('Monday');
    const [selectedStandard, setSelectedStandard] = useState('Standard - 10');
    const [selectedDivision, setSelectedDivision] = useState('Division - A');
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    const scheduleData = [
        { id: 1, sn: '01', time: "09:00 - 10:00 AM", subject: "Mathematics", grade: "Grade 10A", room: "Room 302", type: 'Lecture' },
        { id: 2, sn: '02', time: "10:00 - 11:00 AM", subject: "Mathematics", grade: "Grade 10B", room: "Room 305", type: 'Lecture' },
        { id: 3, sn: '03', time: "11:15 - 12:15 PM", subject: "Physics", grade: "Grade 12B", room: "Lab 02", type: 'Practical' },
        { id: 4, sn: '04', time: "01:00 - 02:00 PM", subject: "Break Interval", grade: "Staff Room", room: "N/A", type: 'Rest' }
    ];

    return (
        <div className="flex h-screen bg-[#F8FAFC] overflow-hidden font-sans">
            <Sidebar />

            <main className="flex-1 overflow-y-auto px-8">
                <Header
                    title="Academic Timetable"
                    subtitle="Viewing schedule for Term 2 • Academic Year 2026"
                    showStandardSelector={true}
                    showDivisionSelector={true}
                    selectedStandard={selectedStandard}
                    selectedDivision={selectedDivision}
                    setSelectedStandard={setSelectedStandard}
                    setSelectedDivision={setSelectedDivision}
                >
                    <button className="flex items-center gap-3 py-2.5 px-6 bg-blue-500 text-white rounded-xl text-[14px] font-bold shadow-lg shadow-blue-100 hover:bg-blue-600 transition-all">
                        <Download size={18} />
                        Export PDF
                    </button>
                </Header>

                {/* Main Content Table Card */}
                <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden flex flex-col mb-10">
                    <div className="p-8 border-b border-gray-50 flex flex-col gap-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-6">
                                <h3 className="text-[20px] font-black text-[#111827]">Daily Class Schedule</h3>
                                <div className="flex items-center gap-2">
                                    <span className="w-2.5 h-2.5 bg-blue-500 rounded-full"></span>
                                    <span className="text-gray-400 font-bold text-[14px]">March 2026 Term</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button className="p-2 text-gray-400 hover:text-gray-900 border border-transparent hover:border-gray-100 rounded-lg transition-all"><ChevronLeft size={20} /></button>
                                <button className="p-2 text-gray-400 hover:text-gray-900 border border-transparent hover:border-gray-100 rounded-lg transition-all"><ChevronRight size={20} /></button>
                            </div>
                        </div>

                        {/* Day Tabs */}
                        <div className="flex gap-2 p-1.5 bg-[#F8FAFC] rounded-[20px] w-fit">
                            {days.map(d => (
                                <button
                                    key={d}
                                    onClick={() => setActiveDay(d)}
                                    className={`px-8 py-3 rounded-[16px] text-[14px] font-black transition-all ${activeDay === d ? 'bg-blue-500 text-white shadow-lg shadow-blue-100' : 'text-gray-400 hover:text-gray-600'}`}
                                >
                                    {d}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-[#F8FAFC]">
                                <tr>
                                    <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-center">No.</th>
                                    <th className="px-6 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase">Timing Interval</th>
                                    <th className="px-6 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase">Subject Assigned</th>
                                    <th className="px-6 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase">Section/Room</th>
                                    <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-center">Lecture Type</th>
                                    <th className="px-8 py-5 text-[12px] font-black text-gray-400 tracking-widest uppercase text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {scheduleData.map((v, i) => (
                                    <tr key={i} className="border-b border-gray-50 last:border-0 hover:bg-slate-50 transition-all group">
                                        <td className="px-8 py-5 text-[14px] font-bold text-gray-500 text-center">{v.sn}</td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-3 text-[15px] font-black text-[#111827]">
                                                <Clock size={16} className="text-gray-400" />
                                                {v.time}
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex items-center gap-4">
                                                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-500 flex items-center justify-center group-hover:bg-blue-500 group-hover:text-white transition-all duration-300">
                                                    <BookOpen size={20} />
                                                </div>
                                                <span className="text-[16px] font-black text-[#111827] group-hover:text-blue-500 transition-colors">{v.subject}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-5">
                                            <div className="flex flex-col gap-0.5">
                                                <p className="text-[14px] font-black text-gray-800 flex items-center gap-2"><Users size={14} className="text-gray-400" /> {v.grade}</p>
                                                <p className="text-[12px] font-bold text-gray-400 flex items-center gap-2"><MapPin size={12} /> {v.room}</p>
                                            </div>
                                        </td>
                                        <td className="px-8 py-5 text-center">
                                            <span className={`text-[11px] font-black px-3 py-1.5 rounded-lg inline-block text-center uppercase tracking-widest ${v.type === 'Lecture' ? 'text-blue-600 bg-blue-50/50' :
                                                v.type === 'Practical' ? 'text-emerald-600 bg-emerald-50/50' :
                                                    'text-gray-400 bg-gray-50'
                                                }`}>
                                                {v.type}
                                            </span>
                                        </td>
                                        <td className="px-8 py-5 text-right">
                                            <button className="p-2 text-gray-400 hover:text-gray-900 transition-colors">
                                                <MoreHorizontal size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="p-8 border-t border-gray-50 flex items-center justify-between bg-[#F8FAFC]/30">
                        <div className="flex items-center gap-2 text-[14px] font-bold text-gray-400">
                            Viewing full week schedule for Grade {selectedStandard.split(' - ')[1]}
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
